import { graphql, useMutation, useQuery } from "@opensea/graphql"
import { Button } from "@opensea/next-ui-kit"
import {
  Chip,
  Flex,
  Input,
  SkeletonLine,
  TextBody,
  useToasts,
} from "@opensea/ui-kit"
import { Mail } from "@opensea/ui-kit/icons"
import { useTranslations } from "next-intl"
import { useDropStore } from "@/components/collection/drops/components/CollectionDropContext"
import { withSuspense } from "@/components/suspense/WithSuspense"
import { useAddress } from "@/hooks/wallet"

const query = graphql(`
  query EmailRemindersQuery($address: Address!) {
    profileByAddress(address: $address) {
      __typename
      ... on Profile {
        email
        emailVerified
      }
    }
  }
`)

const mutation = graphql(`
  mutation EmailRemindersMutation($input: SubscribeToDropStageInput!) {
    subscribeToDropStage(input: $input)
  }
`)

type EmailAddressOptionProps = {
  collectionSlug: string
  selectedStageUuid?: string
}

export const EmailAddressOption = withSuspense(
  function EmailAddressOption({
    collectionSlug,
    selectedStageUuid,
  }: EmailAddressOptionProps) {
    const t = useTranslations("EmailAddressOption")
    const { showSuccessMessage, showErrorMessage } = useToasts()
    const address = useAddress()
    const setIsReminderModalOpen = useDropStore(
      state => state.setIsReminderModalOpen,
    )
    const [{ data }] = useQuery({
      query,
      variables: {
        address: address ?? "",
      },
      pause: !address,
    })
    const [, subscribeToDropStage] = useMutation(mutation)

    if (!data || data.profileByAddress?.__typename !== "Profile") {
      return null
    }

    const email = data?.profileByAddress?.email
    const hasEmail = Boolean(email)
    const isEmailVerified = Boolean(data?.profileByAddress?.emailVerified)

    const handleSubscribe = async () => {
      if (!selectedStageUuid) {
        return
      }

      try {
        const result = await subscribeToDropStage({
          input: {
            collectionSlug,
            dropStageId: selectedStageUuid,
          },
        })

        if (result.data?.subscribeToDropStage) {
          showSuccessMessage(t("subscriptionSuccess"))
          setIsReminderModalOpen(false)
        } else {
          showErrorMessage(t("subscriptionError"))
        }
      } catch {
        showErrorMessage(t("subscriptionError"))
      }
    }

    return (
      <>
        {hasEmail && isEmailVerified ? null : (
          <TextBody color="text-secondary" size="sm">
            {t("addAndVerifyEmail")}
          </TextBody>
        )}
        {email ? (
          <Flex className="gap-2">
            <div className="relative w-full">
              <Input disabled size="sm" value={email} />
              {isEmailVerified ? (
                <Chip className="-translate-y-1/2 absolute top-1/2 right-1 mr-2 border-success-1 bg-transparent text-success-1">
                  {t("verified")}
                </Chip>
              ) : (
                <Chip className="-translate-y-1/2 absolute top-1/2 right-1 mr-2 bg-transparent text-warning">
                  {t("unverified")}
                </Chip>
              )}
            </div>
            {isEmailVerified ? (
              <Button onClick={handleSubscribe} size="sm" variant="secondary">
                {t("add")}
              </Button>
            ) : (
              <Button href="/settings/profile" size="sm" variant="secondary">
                {t("verify")}
              </Button>
            )}
          </Flex>
        ) : (
          <Button
            className="gap-1"
            href="/settings/profile"
            icon={<Mail size={20} />}
            size="sm"
            variant="secondary"
          >
            {t("addEmail")}
          </Button>
        )}
      </>
    )
  },
  {
    fallback: () => {
      return <SkeletonLine className="h-8 w-full" />
    },
  },
)
